import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, ArrowRight, CalendarDays, CheckCircle2, ClipboardCheck, Loader2,
    RefreshCw, Star, Undo2, X,
} from 'lucide-react';
import { Button } from '../Button';
import { CustomerRatingStars } from '../crm/CustomerRatingStars';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import {
    InspectionAssignment,
    formatZeitraum,
} from '../../services/assignmentLogic';
import { Freelancer, formatCents } from '../../services/freelancerLogic';
import {
    AssignmentRating,
    kommentarFehler,
    ratingNote,
    ratingVollstaendig,
    sortiereReviewQueue,
} from '../../services/freelancerInspectionLogic';
import {
    ProjektKopf,
    fetchAssignmentRatings,
    fetchAssignmentStatusLog,
    fetchProjektKoepfe,
    fetchReviewQueue,
    rateAssignment,
    reviewAssignment,
} from '../../services/assignmentService';

// Die Vier-Augen-Prüfung des Innendienstes (ERP/CRM-Plan Abschnitt 7,
// „Workflow" Schritte 4 und 6 — Phase D4).
//
// Dünner Container (Standing Decision): lädt die Warteschlange, die Projekt-
// köpfe und vorhandene Bewertungen EINMAL und reicht sie durch. Reihenfolge,
// Pflichtkommentar und Rating-Arithmetik kommen fertig aus
// freelancerInspectionLogic.ts.
//
// ─── Was hier BEWUSST NICHT steht: eine zweite Berichtsansicht ─────────────
// Der eingereichte Bericht liegt in den ganz normalen Bestandstabellen
// (inspection_items, project_checklists — freelancer_save_inspection schreibt
// dorthin). Der Prüfer öffnet ihn deshalb mit derselben Projektansicht wie
// jeden anderen Bericht; diese Liste bietet dafür nur den Sprung an
// (`onOpenProject`). Eine eigene, reduzierte Prüfansicht wäre eine zweite
// Darstellung derselben Daten — und damit die Gefahr, dass der Prüfer etwas
// freigibt, das er in dieser Form nie gesehen hat.

interface Props {
    /** Stammsätze für die Namensauflösung — die Ansicht lädt sie nicht selbst. */
    freelancers: Freelancer[];
    /** Sprung in die Projektansicht (der eigentliche Bericht). */
    onOpenProject?: (projectId: string) => void;
    /** Nach Freigabe/Bewertung: die Freelancer-Liste des Aufrufers auffrischen. */
    onChanged?: () => void;
}

interface Bewertung {
    qualitaet: number;
    zuverlaessigkeit: number;
    kommentar: string;
}

const LEER: Bewertung = { qualitaet: 0, zuverlaessigkeit: 0, kommentar: '' };

export const ReviewQueue: React.FC<Props> = ({ freelancers, onOpenProject, onChanged }) => {
    const [queue, setQueue] = useState<InspectionAssignment[]>([]);
    const [projekte, setProjekte] = useState<Map<string, ProjektKopf>>(new Map());
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [arbeitetAn, setArbeitetAn] = useState<string | null>(null);

    /** Offenes Nachbesserungs-Formular: assignmentId -> Kommentar. */
    const [nachbesserung, setNachbesserung] = useState<Record<string, string>>({});
    /** Nach der Freigabe erscheint direkt das 1-Klick-Rating. */
    const [bewertungAn, setBewertungAn] = useState<InspectionAssignment | null>(null);
    const [bewertung, setBewertung] = useState<Bewertung>(LEER);
    const [vorhandene, setVorhandene] = useState<Map<string, AssignmentRating>>(new Map());
    /** Statushistorie einer aufgeklappten Zeile (zeigt frühere Prüfkommentare). */
    const [historieAn, setHistorieAn] = useState<string | null>(null);
    const [historie, setHistorie] = useState<{ status: string; comment?: string; createdAt: string }[]>([]);

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            const offen = await fetchReviewQueue();
            setQueue(offen);
            // Projektnamen und bereits vergebene Bewertungen in je EINER
            // Abfrage — eine je Zeile wäre ein N+1 auf einer Liste, die ohnehin
            // alles anzeigt.
            const [koepfe, ratings] = await Promise.all([
                fetchProjektKoepfe(offen.map((a) => a.projectId)),
                fetchAssignmentRatings(offen.map((a) => a.id)),
            ]);
            setProjekte(koepfe);
            setVorhandene(ratings);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLaedt(false);
        }
    }, []);

    useEffect(() => {
        void laden();
    }, [laden]);

    const sortiert = useMemo(() => sortiereReviewQueue(queue), [queue]);
    const namen = useMemo(() => {
        const m = new Map<string, string>();
        for (const f of freelancers) m.set(f.id, f.name);
        return m;
    }, [freelancers]);

    const historieOeffnen = async (a: InspectionAssignment) => {
        if (historieAn === a.id) {
            setHistorieAn(null);
            return;
        }
        setHistorieAn(a.id);
        setHistorie([]);
        try {
            const eintraege = await fetchAssignmentStatusLog(a.id);
            setHistorie(eintraege.map((e) => ({
                status: e.newStatus,
                comment: e.comment,
                createdAt: e.createdAt,
            })));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        }
    };

    const freigeben = async (a: InspectionAssignment) => {
        setArbeitetAn(a.id);
        setFehler(null);
        try {
            await reviewAssignment(a.id, 'geprueft');
            setQueue((prev) => prev.filter((x) => x.id !== a.id));
            // Direkt ins Rating: der Moment nach der Freigabe ist der einzige,
            // in dem der Prüfer die Leistung frisch im Kopf hat.
            setBewertung(LEER);
            setBewertungAn({ ...a, status: 'geprueft' });
            onChanged?.();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setArbeitetAn(null);
        }
    };

    const zurueckgeben = async (a: InspectionAssignment) => {
        const kommentar = nachbesserung[a.id] || '';
        const problem = kommentarFehler('nachbesserung', kommentar);
        if (problem) {
            setFehler(problem);
            return;
        }
        setArbeitetAn(a.id);
        setFehler(null);
        try {
            await reviewAssignment(a.id, 'nachbesserung', kommentar);
            setQueue((prev) => prev.filter((x) => x.id !== a.id));
            setNachbesserung((prev) => {
                const kopie = { ...prev };
                delete kopie[a.id];
                return kopie;
            });
            onChanged?.();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setArbeitetAn(null);
        }
    };

    const bewertungSpeichern = async () => {
        if (!bewertungAn) return;
        setArbeitetAn(bewertungAn.id);
        setFehler(null);
        try {
            await rateAssignment(
                bewertungAn.id,
                bewertung.qualitaet,
                bewertung.zuverlaessigkeit,
                bewertung.kommentar,
            );
            setBewertungAn(null);
            setBewertung(LEER);
            // Der Trigger hat freelancers.rating nachgeführt — der Aufrufer lädt
            // seine Stammdatenliste neu, statt den Wert hier nachzurechnen.
            onChanged?.();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setArbeitetAn(null);
        }
    };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col lg:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-4 w-full lg:w-auto">
                    <div className="p-3 bg-violet-50 text-violet-600 rounded-2xl">
                        <ClipboardCheck className="w-8 h-8" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Prüfung</h2>
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">
                            {sortiert.length} {sortiert.length === 1 ? 'Bericht wartet' : 'Berichte warten'} auf die Vier-Augen-Prüfung
                        </p>
                    </div>
                </div>
                <Button
                    variant="secondary"
                    onClick={laden}
                    icon={<RefreshCw className={`w-4 h-4 ${laedt ? 'animate-spin' : ''}`} />}
                >
                    Neu laden
                </Button>
            </div>

            {fehler && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl p-4 text-sm flex items-start justify-between gap-3">
                    <span className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        {fehler}
                    </span>
                    <button onClick={() => setFehler(null)} className="shrink-0 hover:bg-rose-100 rounded p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {laedt ? (
                <div className="py-24 text-center text-slate-400 bg-white rounded-3xl border border-slate-200">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
                    <p className="text-sm font-medium">Prüfliste wird geladen…</p>
                </div>
            ) : sortiert.length === 0 ? (
                <div className="py-20 text-center text-slate-400 bg-white rounded-3xl border-2 border-dashed border-slate-100">
                    <div className="bg-slate-50 p-4 rounded-full w-fit mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                    </div>
                    <p className="font-bold text-slate-600">Nichts offen</p>
                    <p className="text-xs mt-1">Es liegt kein eingereichter Begehungsbericht zur Prüfung.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {sortiert.map((a) => {
                        const projekt = projekte.get(a.projectId);
                        const offenesFormular = nachbesserung[a.id] !== undefined;
                        return (
                            <div key={a.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h4 className="font-bold text-slate-800 text-sm truncate">
                                            {projekt?.name || 'Projekt'}
                                        </h4>
                                        <p className="text-[11px] text-slate-400">
                                            {projekt?.projectNumber ? `${projekt.projectNumber} · ` : ''}
                                            {namen.get(a.freelancerId) || 'Freelancer'}
                                        </p>
                                    </div>
                                    <span className="text-[11px] text-slate-500 shrink-0 flex items-center gap-1.5">
                                        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                                        {formatZeitraum(a.zeitraumVon, a.zeitraumBis)}
                                    </span>
                                </div>

                                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
                                    <span className="font-semibold">{formatCents(a.verguetungCents)}</span>
                                    <button
                                        onClick={() => void historieOeffnen(a)}
                                        className="text-[11px] font-bold text-slate-400 hover:text-slate-700 underline underline-offset-2"
                                    >
                                        {historieAn === a.id ? 'Verlauf ausblenden' : 'Verlauf'}
                                    </button>
                                    {onOpenProject && (
                                        <button
                                            onClick={() => onOpenProject(a.projectId)}
                                            className="ml-auto text-[11px] font-bold text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                                        >
                                            Bericht im Projekt öffnen
                                            <ArrowRight className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>

                                {historieAn === a.id && (
                                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1.5">
                                        {historie.length === 0 ? (
                                            <p className="text-[11px] text-slate-400 italic">Verlauf wird geladen…</p>
                                        ) : (
                                            historie.map((h, idx) => (
                                                <p key={idx} className="text-[11px] text-slate-600">
                                                    <span className="font-mono text-slate-400">
                                                        {new Date(h.createdAt).toLocaleDateString('de-DE')}
                                                    </span>
                                                    {' — '}
                                                    <b>{h.status}</b>
                                                    {h.comment && <span className="text-slate-500">: {h.comment}</span>}
                                                </p>
                                            ))
                                        )}
                                    </div>
                                )}

                                {offenesFormular ? (
                                    <div className="space-y-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                                        <label className="text-[11px] font-bold text-amber-800 uppercase tracking-widest">
                                            Was ist nachzuarbeiten? (Pflicht)
                                        </label>
                                        <textarea
                                            autoFocus
                                            rows={3}
                                            value={nachbesserung[a.id]}
                                            onChange={(e) =>
                                                setNachbesserung((prev) => ({ ...prev, [a.id]: e.target.value }))}
                                            placeholder="z. B. Fotos zum Gerüst fehlen, Frist für Mangel 3 nicht gesetzt…"
                                            className="w-full px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-amber-500/20"
                                        />
                                        <p className="text-[11px] text-amber-700">
                                            Der Freelancer sieht genau diesen Text in seinem Portal.
                                        </p>
                                        <div className="flex gap-2 justify-end">
                                            <Button
                                                variant="ghost"
                                                onClick={() =>
                                                    setNachbesserung((prev) => {
                                                        const kopie = { ...prev };
                                                        delete kopie[a.id];
                                                        return kopie;
                                                    })}
                                                className="text-xs py-1.5"
                                            >
                                                Abbrechen
                                            </Button>
                                            <Button
                                                variant="danger"
                                                onClick={() => void zurueckgeben(a)}
                                                disabled={arbeitetAn === a.id}
                                                icon={
                                                    arbeitetAn === a.id
                                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        : <Undo2 className="w-3.5 h-3.5" />
                                                }
                                                className="text-xs py-1.5"
                                            >
                                                Nachbesserung anfordern
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2 justify-end pt-1">
                                        <Button
                                            variant="secondary"
                                            onClick={() => setNachbesserung((prev) => ({ ...prev, [a.id]: '' }))}
                                            disabled={arbeitetAn === a.id}
                                            icon={<Undo2 className="w-3.5 h-3.5" />}
                                            className="text-xs py-1.5"
                                        >
                                            Nachbesserung anfordern
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={() => void freigeben(a)}
                                            disabled={arbeitetAn === a.id}
                                            icon={
                                                arbeitetAn === a.id
                                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    : <CheckCircle2 className="w-3.5 h-3.5" />
                                            }
                                            className="text-xs py-1.5"
                                        >
                                            Freigeben (geprüft)
                                        </Button>
                                    </div>
                                )}

                                {vorhandene.has(a.id) && (
                                    <p className="text-[11px] text-slate-400">
                                        Bereits bewertet mit{' '}
                                        {ratingNote(
                                            vorhandene.get(a.id)!.qualitaet,
                                            vorhandene.get(a.id)!.zuverlaessigkeit,
                                        )}
                                        {' '}von 5.
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── 1-Klick-Bewertung direkt nach der Freigabe ── */}
            {bewertungAn && (
                <div className="fixed inset-0 z-[110] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">Begehung bewerten</h3>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {namen.get(bewertungAn.freelancerId) || 'Freelancer'} ·{' '}
                                    {formatZeitraum(bewertungAn.zeitraumVon, bewertungAn.zeitraumBis)}
                                </p>
                            </div>
                            <button
                                onClick={() => { setBewertungAn(null); setBewertung(LEER); }}
                                className="p-1.5 text-slate-400 hover:text-slate-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-sm font-semibold text-slate-700">Qualität</span>
                                <CustomerRatingStars
                                    value={bewertung.qualitaet}
                                    onChange={(v) => setBewertung((b) => ({ ...b, qualitaet: v }))}
                                />
                            </div>
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-sm font-semibold text-slate-700">Zuverlässigkeit</span>
                                <CustomerRatingStars
                                    value={bewertung.zuverlaessigkeit}
                                    onChange={(v) => setBewertung((b) => ({ ...b, zuverlaessigkeit: v }))}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                                    Bemerkung (optional, intern)
                                </label>
                                <textarea
                                    rows={2}
                                    value={bewertung.kommentar}
                                    onChange={(e) => setBewertung((b) => ({ ...b, kommentar: e.target.value }))}
                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20"
                                />
                            </div>
                            {ratingVollstaendig(bewertung.qualitaet, bewertung.zuverlaessigkeit) && (
                                <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                                    <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                    Diese Begehung geht mit{' '}
                                    {ratingNote(bewertung.qualitaet, bewertung.zuverlaessigkeit)} von 5 in den
                                    Durchschnitt ein.
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => { setBewertungAn(null); setBewertung(LEER); }}
                            >
                                Später
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => void bewertungSpeichern()}
                                disabled={
                                    !ratingVollstaendig(bewertung.qualitaet, bewertung.zuverlaessigkeit)
                                    || arbeitetAn === bewertungAn.id
                                }
                                icon={
                                    arbeitetAn === bewertungAn.id
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : <Star className="w-4 h-4" />
                                }
                            >
                                Bewertung speichern
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
